<script setup lang="ts">
import { ref, computed, onMounted } from 'vue';
import { useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import axiosInstance from '@/services/interceptor.services';

const router = useRouter();
const { t, locale } = useI18n({ useScope: 'global' });

const title = ref('');
const content = ref('');
const version = ref<number | null>(null);
const effectiveFrom = ref<string | null>(null);
const isLoading = ref(true);
const loadError = ref(false);

async function load() {
  isLoading.value = true;
  loadError.value = false;
  try {
    const res = await axiosInstance.get('legal/LEGAL_NOTICE', {
      headers: {
        'accept-language': locale.value,
        'x-client-app': 'admin-back-office',
      },
    });
    const data = res.data?.data ?? res.data;
    title.value = data?.title ?? t('terms.title');
    content.value = data?.content ?? '';
    version.value = data?.version ?? null;
    effectiveFrom.value = data?.effectiveFrom ?? null;
  } catch {
    loadError.value = true;
  } finally {
    isLoading.value = false;
  }
}

const rendered = computed(() => {
  if (!content.value) return '';
  return content.value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/^### (.+)$/gm, '<h3>$1</h3>')
    .replace(/^## (.+)$/gm, '<h2>$1</h2>')
    .replace(/^# (.+)$/gm, '<h1>$1</h1>')
    .replace(/\*\*\*(.+?)\*\*\*/g, '<strong><em>$1</em></strong>')
    .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
    .replace(/\*(.+?)\*/g, '<em>$1</em>')
    .replace(/`([^`]+)`/g, '<code>$1</code>')
    .replace(/^[-*] (.+)$/gm, '<li>$1</li>')
    .replace(/^\d+\. (.+)$/gm, '<oli>$1</oli>')
    .replace(/^---$/gm, '<hr>')
    .split(/\n{2,}/)
    .map((block) => {
      block = block.trim();
      if (!block) return '';
      if (/^<(h[1-6]|hr|ul|ol|li|oli)/.test(block)) return block;
      if (block.includes('<li>')) return '<ul>' + block + '</ul>';
      if (block.includes('<oli>'))
        return (
          '<ol>' +
          block.replace(/<\/?oli>/g, (m) =>
            m === '<oli>' ? '<li>' : '</li>',
          ) +
          '</ol>'
        );
      return '<p>' + block.replace(/\n/g, '<br>') + '</p>';
    })
    .join('\n');
});

const formattedDate = computed(() => {
  if (!effectiveFrom.value) return null;
  try {
    return new Date(effectiveFrom.value).toLocaleDateString(locale.value, {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  } catch {
    return null;
  }
});

onMounted(load);
</script>

<template>
  <div class="terms-wrap">
    <div class="terms">
      <header class="terms__header">
        <button class="terms__back" @click="router.back()">
          <i class="pi pi-arrow-left" /> {{ t('common.back') }}
        </button>

        <div v-if="isLoading" class="terms__skeleton">
          <div class="terms__skeleton-title" />
          <div class="terms__skeleton-line" />
          <div class="terms__skeleton-line terms__skeleton-line--short" />
        </div>

        <template v-else-if="!loadError">
          <h1 class="terms__title">{{ title || t('terms.title') }}</h1>
          <p v-if="formattedDate" class="terms__updated">
            {{ t('common.from') }} {{ formattedDate }}
          </p>
          <span v-if="version" class="terms__version-badge">
            {{ t('terms.versionBadge', { v: version }) }}
          </span>
        </template>
      </header>

      <!-- Loading skeleton body -->
      <div v-if="isLoading" class="terms__body">
        <div v-for="i in 4" :key="i" class="terms__section">
          <div class="terms__skeleton-h2" />
          <div class="terms__skeleton-line" />
          <div class="terms__skeleton-line" />
          <div class="terms__skeleton-line terms__skeleton-line--short" />
        </div>
      </div>

      <!-- Error state -->
      <div v-else-if="loadError" class="terms__error">
        <i class="pi pi-exclamation-triangle" />
        <p>{{ t('terms.loadError') }}</p>
        <button class="terms__retry" @click="load">
          {{ t('common.retry') }}
        </button>
      </div>

      <!-- Content -->
      <section v-else class="terms__body">
        <div v-if="!content" class="terms__empty">
          <i class="pi pi-file" />
          <p>{{ t('terms.editorPlaceholder') }}</p>
        </div>
        <div v-else class="terms__content" v-html="rendered" />
      </section>

      <footer class="terms__footer">
        <p>© 2026 Nextep Solutions.</p>
      </footer>
    </div>
  </div>
</template>

<style scoped>
.terms-wrap {
  min-height: 100dvh;
  background-color: var(--nxt-background, #f8f9fa);
  display: flex;
  justify-content: center;
  padding: 3rem 1.5rem;
}

.terms {
  width: 100%;
  max-width: 720px;
  display: flex;
  flex-direction: column;
  gap: 2rem;
}

/* Header */
.terms__header {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.terms__back {
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
  background: none;
  border: none;
  color: var(--nxt-primary);
  font-size: 0.875rem;
  font-weight: 600;
  font-family: inherit;
  cursor: pointer;
  padding: 0;
  margin-bottom: 0.5rem;
  width: fit-content;
}
.terms__back:hover {
  text-decoration: underline;
}

.terms__title {
  margin: 0;
  font-size: 2rem;
  font-weight: 700;
  color: var(--nxt-on-surface, #191c1d);
  letter-spacing: -0.01em;
}

.terms__updated {
  margin: 0;
  font-size: 0.875rem;
  color: var(--nxt-on-surface-muted, #938282);
}

.terms__version-badge {
  display: inline-flex;
  align-items: center;
  height: 1.35rem;
  padding: 0 0.5rem;
  border-radius: 9999px;
  font-size: 0.625rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.07em;
  background: rgba(0, 0, 0, 0.07);
  color: var(--nxt-on-surface-variant);
  width: fit-content;
}

/* Body */
.terms__body {
  display: flex;
  flex-direction: column;
  gap: 1.75rem;
}

.terms__section {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

/* Markdown content */
.terms__content :deep(h1) {
  font-size: 1.75rem;
  font-weight: 800;
  letter-spacing: -0.02em;
  margin: 0 0 1rem;
  color: var(--nxt-on-surface);
  line-height: 1.2;
}
.terms__content :deep(h2) {
  font-size: 1.125rem;
  font-weight: 600;
  margin: 0 0 0.4rem;
  color: var(--nxt-on-surface, #191c1d);
}
.terms__content :deep(h3) {
  font-size: 1rem;
  font-weight: 600;
  margin: 1rem 0 0.35rem;
  color: var(--nxt-on-surface);
}
.terms__content :deep(p) {
  margin: 0 0 1rem;
  font-size: 0.9375rem;
  color: var(--nxt-on-surface-variant, #564335);
  line-height: 1.7;
}
.terms__content :deep(strong) {
  font-weight: 700;
  color: var(--nxt-on-surface);
}
.terms__content :deep(em) {
  font-style: italic;
}
.terms__content :deep(code) {
  font-family: 'Menlo', 'Consolas', monospace;
  font-size: 0.825rem;
  background: var(--nxt-surface-low, rgba(0, 0, 0, 0.05));
  padding: 0.1em 0.35em;
  border-radius: 0.25rem;
  color: var(--nxt-primary);
}
.terms__content :deep(ul) {
  margin: 0 0 1rem 1.25rem;
  padding: 0;
  list-style: disc;
}
.terms__content :deep(ol) {
  margin: 0 0 1rem 1.25rem;
  padding: 0;
  list-style: decimal;
}
.terms__content :deep(li) {
  margin-bottom: 0.35rem;
  font-size: 0.9375rem;
  color: var(--nxt-on-surface-variant);
  line-height: 1.7;
}
.terms__content :deep(hr) {
  border: none;
  border-top: 1px solid var(--nxt-outline-variant, rgba(147, 130, 130, 0.2));
  margin: 1.5rem 0;
}

/* Error */
.terms__error {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.75rem;
  padding: 3rem 1rem;
  color: var(--nxt-on-surface-variant);
  text-align: center;
}
.terms__error i {
  font-size: 2rem;
  color: #dc2626;
  opacity: 0.6;
}
.terms__error p {
  margin: 0;
  font-size: 0.9375rem;
}
.terms__retry {
  background: none;
  border: 1.5px solid var(--nxt-outline-variant, #e0e0e0);
  border-radius: 0.5rem;
  padding: 0.4rem 1rem;
  font-size: 0.8125rem;
  font-weight: 600;
  font-family: inherit;
  color: var(--nxt-on-surface);
  cursor: pointer;
}
.terms__retry:hover {
  background: var(--nxt-surface-low);
}

/* Empty */
.terms__empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5rem;
  padding: 3rem 1rem;
  color: var(--nxt-on-surface-variant);
  text-align: center;
  opacity: 0.5;
}
.terms__empty i {
  font-size: 2rem;
}

/* Skeleton */
.terms__skeleton {
  display: flex;
  flex-direction: column;
  gap: 0.6rem;
}
.terms__skeleton-title {
  height: 2.25rem;
  width: 60%;
  background: var(--nxt-surface-low, rgba(0, 0, 0, 0.07));
  border-radius: 0.5rem;
  animation: pulse 1.4s ease-in-out infinite;
}
.terms__skeleton-h2 {
  height: 1.25rem;
  width: 35%;
  background: var(--nxt-surface-low, rgba(0, 0, 0, 0.07));
  border-radius: 0.375rem;
  animation: pulse 1.4s ease-in-out infinite;
}
.terms__skeleton-line {
  height: 0.875rem;
  width: 100%;
  background: var(--nxt-surface-low, rgba(0, 0, 0, 0.07));
  border-radius: 0.375rem;
  animation: pulse 1.4s ease-in-out infinite;
}
.terms__skeleton-line--short {
  width: 70%;
}

@keyframes pulse {
  0%,
  100% {
    opacity: 1;
  }
  50% {
    opacity: 0.4;
  }
}

/* Footer */
.terms__footer {
  padding-top: 1rem;
  border-top: 1px solid var(--nxt-outline-variant, rgba(147, 130, 130, 0.2));
  font-size: 0.8125rem;
  color: var(--nxt-on-surface-muted, #938282);
  text-align: center;
}

/* Dark mode */
[data-theme='dark'] .terms__version-badge {
  background: rgba(255, 255, 255, 0.1);
}
</style>
